// src/components/admin/BulkSubcategoryModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Save, Layers, Folder, ArrowRight } from 'lucide-react';
import { CategoryTreeNode } from '../../types/category';

interface BulkSubcategoryModalProps {
  isOpen: boolean;
  parentCategory: CategoryTreeNode | null;
  onClose: () => void;
  onSave: (subcategories: { name: string; description: string }[]) => void;
}

const BulkSubcategoryModal: React.FC<BulkSubcategoryModalProps> = ({ 
  isOpen, 
  parentCategory, 
  onClose, 
  onSave 
}) => {
  const [subcategoryText, setSubcategoryText] = useState('');
  const [parsedSubcategories, setParsedSubcategories] = useState<{ name: string; description: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSubcategoryText('');
      setParsedSubcategories([]);
    }
  }, [isOpen]);

  // Parse subcategories from text input
  useEffect(() => {
    const lines = subcategoryText.split('\n').filter(line => line.trim());
    const parsed = lines.map(line => {
      const trimmed = line.trim();
      // Check if line contains a description after " - " or " | " or " : "
      const separators = [' - ', ' | ', ' : '];
      let name = trimmed;
      let description = '';
      
      for (const sep of separators) {
        if (trimmed.includes(sep)) {
          const parts = trimmed.split(sep, 2);
          name = parts[0].trim();
          description = parts[1].trim();
          break;
        }
      }
      
      return { name, description };
    });
    
    setParsedSubcategories(parsed);
  }, [subcategoryText]);

  const handleSave = async () => {
    if (parsedSubcategories.length === 0) return;
    
    setLoading(true);
    try {
      await onSave(parsedSubcategories);
      onClose();
    } catch (error) {
      console.error('Error saving subcategories:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !parentCategory) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Layers className="w-5 h-5 text-blue-600 mr-3" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Bulk Add Subcategories
              </h2>
              <p className="text-sm text-gray-600">
                Adding to: <span className="font-medium">{parentCategory.name}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="text-sm font-medium text-blue-900 mb-2">How to use:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Enter one subcategory per line</li>
              <li>• Add descriptions using " - " separator (e.g., "Handguns - Service pistols")</li>
              <li>• You can also use " | " or " : " as separators</li>
              <li>• Empty lines will be ignored</li>
            </ul>
          </div>

          {/* Text Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategories (one per line)
            </label>
            <textarea
              value={subcategoryText}
              onChange={(e) => setSubcategoryText(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Handguns - Service pistols and revolvers
Rifles - Patrol rifles and long guns
Shotguns - Tactical shotguns
Accessories - Holsters, magazines, and attachments`}
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              {subcategoryText.split('\n').filter(line => line.trim()).length} subcategories detected
            </p>
          </div>

          {/* Preview */}
          {parsedSubcategories.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Preview:</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                {parsedSubcategories.map((sub, index) => (
                  <div key={index} className="flex items-center py-2 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center flex-1">
                      <div className="w-8 h-8 bg-gray-200 rounded-md flex items-center justify-center mr-3">
                        <Folder className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-400 text-sm mr-2">{parentCategory.name}</span>
                        <ArrowRight className="w-3 h-3 text-gray-400 mr-2" />
                        <div>
                          <span className="font-medium text-gray-900">{sub.name}</span>
                          {sub.description && (
                            <span className="text-sm text-gray-500 ml-2">- {sub.description}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || parsedSubcategories.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Creating...' : `Create ${parsedSubcategories.length} Subcategories`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkSubcategoryModal;